import { Link } from 'react-router'
import { Card, CategoryChip, EmptyState, ImpactBadge, Skeleton } from '../../ui'
import { formatNumber } from '../../lib/format'
import { Kpi, MiniBars, Pill, secs, when, useAdmin } from './shared'
import styles from './admin.module.css'

interface Pipeline {
  hourly: Array<{ hour: number; exchange: string; n: number }>
  recent: Array<{ id: number; symbol: string; exchange: string; headline: string; filed_at: string | null; created_at: string; dup_of: number | null; category: string | null; impact_score: number | null; classified_at: string | null; fetch_lag_s: number | null }>
  unclassified_24h: number
  categories_today: Array<{ category: string; n: number }>
  fetch_lag_s: { p50: number | null; p95: number | null }
}
interface Latency { hours: number; sent: number; p50_ms: number | null; p95_ms: number | null; ingest_p50_ms: number | null; ingest_p95_ms: number | null; by_status: Array<{ status: string; reason: string; channel: string; n: number }> }
interface AdminAlerts {
  latency_24h: Latency; latency_7d: Latency
  counts: { active_rules: number; dormant_migrated_rules: number; telegram_linked: number; users_with_holdings: number; risk_flags_7d: number }
  recent: Array<{ id: number; kind: string; channel: string; status: string; error: string | null; created_at: string; latency_ms: number | null; ingest_latency_ms: number | null; symbol: string | null; category: string | null }>
}

export function PipelineTab() {
  const { data: p, error } = useAdmin<Pipeline>('/admin/pipeline', 30000)
  const { data: a } = useAdmin<AdminAlerts>('/admin/alerts', 30000)
  if (error && !p) return <Card variant="flush"><EmptyState kind="error" title="Could not load the pipeline" compact>{error}</EmptyState></Card>
  if (!p) return <Card><Skeleton lines={5} label="Loading pipeline" /></Card>
  const hours = Array.from({ length: 24 }, (_, h) => ({ key: String(h), value: p.hourly.filter(x => x.hour === h).reduce((s, x) => s + x.n, 0), label: `${String(h).padStart(2, '0')}:00 IST` }))
  const d = a?.latency_24h
  return (
    <div className={styles.section}>
      <section aria-label="Pipeline numbers" className={styles.kpis}>
        <Kpi label="Exchange → BCEIE p50" value={p.fetch_lag_s.p50 == null ? '—' : secs(p.fetch_lag_s.p50 * 1000)} hint={`p95 ${p.fetch_lag_s.p95 == null ? '—' : secs(p.fetch_lag_s.p95 * 1000)} · today`} />
        <Kpi label="Unclassified (24 h)" value={p.unclassified_24h} hint="waiting for the classifier" />
        <Kpi label="Filing → alert p50 (24 h)" value={secs(d?.p50_ms)} hint={d ? `p95 ${secs(d.p95_ms)} · ${d.sent} alerts` : undefined} />
        <Kpi label="Ingest → alert p50 (24 h)" value={secs(d?.ingest_p50_ms)} hint={d ? `p95 ${secs(d.ingest_p95_ms)}` : undefined} />
        <Kpi label="Active alert rules" value={a?.counts.active_rules ?? '—'} hint={a ? `${a.counts.telegram_linked} Telegram linked · ${a.counts.risk_flags_7d} risk flags (7 d)` : undefined} />
      </section>

      <div className={styles.grid2}>
        <Card title="Filings received today, by hour (IST)">
          <MiniBars label="Filings per hour" data={hours} format={n => formatNumber(n, 0)} xLabel={['00:00', '23:00']} />
        </Card>
        <Card title="Today by category" variant="flush">
          {p.categories_today.length === 0 ? <EmptyState title="No filings yet today" compact /> : (
            <table className={styles.table}><caption className="sr-only">Filings by category today</caption>
              <thead><tr><th scope="col">Category</th><th scope="col" className={styles.thNum}>Filings</th></tr></thead>
              <tbody>{p.categories_today.slice(0, 10).map(c => <tr key={c.category}><td>{c.category === 'unclassified' ? <span className={styles.muted}>unclassified</span> : <CategoryChip category={c.category} size="sm" />}</td><td className={styles.num}>{c.n}</td></tr>)}</tbody>
            </table>
          )}
        </Card>
      </div>

      <Card title="Latest filings through the pipeline" variant="flush" action={<Link to="/announcements">Announcements →</Link>}>
        <div className={styles.tableWrap} tabIndex={0} role="region" aria-label="Latest filings (scrolls sideways)">
          <table className={styles.table}><caption className="sr-only">Latest filings</caption>
            <thead><tr><th scope="col">Received</th><th scope="col">Stock</th><th scope="col">Headline</th><th scope="col">Category</th><th scope="col">Impact</th><th scope="col" className={styles.thNum}>Exchange → us</th><th scope="col">State</th></tr></thead>
            <tbody>{p.recent.map(r => (
              <tr key={r.id}>
                <td className={styles.muted}>{when(r.created_at)}</td>
                <td><Link className={styles.sym} to={`/stock/${encodeURIComponent(r.symbol)}`}>{r.symbol}</Link> <span className={styles.muted}>{r.exchange}</span></td>
                <td className={styles.wrap}>{r.headline}</td>
                <td>{r.category ? <CategoryChip category={r.category} size="sm" /> : '—'}</td>
                <td>{r.impact_score != null ? <ImpactBadge score={r.impact_score} size="sm" /> : '—'}</td>
                <td className={styles.num}>{r.fetch_lag_s == null ? '—' : secs(r.fetch_lag_s * 1000)}</td>
                <td>{r.dup_of ? <Pill tone="off">Duplicate</Pill> : r.classified_at ? <Pill tone="ok">Classified</Pill> : <Pill tone="warn">Pending</Pill>}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </Card>

      {a && (
        <div className={styles.grid2}>
          <Card title="Alert deliveries by status (24 h)" variant="flush">
            {a.latency_24h.by_status.length === 0 ? <EmptyState title="No deliveries in the last 24 hours" compact /> : (
              <table className={styles.table}><caption className="sr-only">Deliveries by status</caption>
                <thead><tr><th scope="col">Status</th><th scope="col">Reason</th><th scope="col">Channel</th><th scope="col" className={styles.thNum}>Count</th></tr></thead>
                <tbody>{a.latency_24h.by_status.map((s, i) => <tr key={i}><td>{s.status}</td><td>{s.reason || '—'}</td><td>{s.channel}</td><td className={styles.num}>{s.n}</td></tr>)}</tbody>
              </table>
            )}
          </Card>
          <Card title="Latest alert deliveries" variant="flush">
            {a.recent.length === 0 ? <EmptyState title="Nothing yet" compact /> : (
              <div className={styles.tableWrap} tabIndex={0} role="region" aria-label="Latest deliveries (scrolls sideways)">
                <table className={styles.table}><caption className="sr-only">Latest deliveries</caption>
                  <thead><tr><th scope="col">When</th><th scope="col">Stock</th><th scope="col">Channel</th><th scope="col">Status</th><th scope="col" className={styles.thNum}>Filing → alert</th></tr></thead>
                  <tbody>{a.recent.slice(0, 15).map(r => <tr key={r.id}><td className={styles.muted}>{when(r.created_at)}</td><td>{r.symbol ?? '—'}</td><td>{r.channel} · {r.kind}</td><td>{r.status}{r.error ? ` · ${r.error}` : ''}</td><td className={styles.num}>{secs(r.latency_ms)}</td></tr>)}</tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  )
}
